import { StyleSheet } from 'aphrodite';
import { SPACE, THEME } from '../../../../web/components-v2/Theme/theme';
import { shadowSm } from '../../../../web/styles/shadows';

const { fontSizes, fontWeights } = THEME.typography;

export const CALL_LOG_CONTAINER = 'callLog';

// Rows follow the list's width, not the viewport, because the side drawer can take up to 800px of it.
const containerBelow = (width: number) => `@container ${CALL_LOG_CONTAINER} (max-width: ${width - 1}px)`;
const BELOW_FULL_ROW = containerBelow(860);
const BELOW_COLUMNS = containerBelow(660);
const BELOW_TWO_CARD_COLUMNS = containerBelow(420);

const CALL_LOG_COLUMNS =
	'minmax(150px, 1.3fr) minmax(110px, 1fr) minmax(150px, 1fr) minmax(180px, 1fr) minmax(160px, 1.4fr)';
const CALL_LOG_COLUMNS_NARROW = 'minmax(130px, 1.3fr) minmax(100px, 1fr) minmax(150px, 1fr) minmax(180px, 1fr)';
const INCOMING_COLUMNS = 'minmax(150px, 1.3fr) minmax(130px, 1fr) minmax(150px, 1fr) minmax(120px, 1fr)';
const CARD_COLUMNS = {
	[BELOW_COLUMNS]: {
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	},
	[BELOW_TWO_CARD_COLUMNS]: {
		gridTemplateColumns: 'minmax(0, 1fr)',
	},
};

export const styleSheet = StyleSheet.create({
	toolbar: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[12],
		justifyContent: 'space-between',
	},
	controls: {
		alignItems: 'center',
		display: 'flex',
		flex: '1 1 auto',
		flexWrap: 'wrap',
		gap: SPACE[12],
		minWidth: 0,
		[THEME.queries.xl]: {
			display: 'contents',
		},
	},
	sdr: {
		flex: '0 1 220px',
		minWidth: 160,
		[THEME.queries.xl]: {
			flex: '1 1 30%',
			minWidth: 0,
		},
		[THEME.queries.sm]: {
			flexBasis: '100%',
		},
	},
	dateRange: {
		display: 'grid',
		flex: '0 1 260px',
		[THEME.queries.xl]: {
			flex: '1 1 30%',
		},
		[THEME.queries.sm]: {
			flexBasis: '100%',
		},
	},
	outcome: {
		flex: '0 1 200px',
		minWidth: 170,
		[THEME.queries.xl]: {
			flex: '1 1 30%',
			minWidth: 0,
		},
		[THEME.queries.sm]: {
			flexBasis: '100%',
		},
	},
	export: {
		marginLeft: 'auto',
		[THEME.queries.md]: {
			display: 'none',
		},
	},
	moreFilters: {
		boxSizing: 'border-box',
		display: 'grid',
		gap: `${SPACE[12]}px ${SPACE[16]}px`,
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		padding: SPACE[16],
		width: 456,
		[THEME.queries.sm]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
			width: 'calc(100vw - 32px)',
		},
	},
	inlineFilters: {
		display: 'grid',
		flexBasis: '100%',
		gap: `${SPACE[12]}px ${SPACE[16]}px`,
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		[THEME.queries.lg]: {
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		},
		[THEME.queries.sm]: {
			gridTemplateColumns: 'minmax(0, 1fr)',
		},
	},
	inlineActions: {
		alignItems: 'center',
		display: 'flex',
		flexBasis: '100%',
		gap: SPACE[12],
	},
	moreFiltersFooter: {
		display: 'flex',
		gridColumn: '1 / -1',
		justifyContent: 'flex-end',
	},
	field: {
		minWidth: 0,
	},
	selectTrigger: {
		backgroundColor: THEME.colors.background.light,
		boxSizing: 'border-box',
		width: '100%',
	},
	chips: {
		alignItems: 'center',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[8],
	},
	columns: {
		color: THEME.colors.text.light,
		display: 'grid',
		gap: SPACE[16],
		fontSize: fontSizes.xs,
		fontWeight: fontWeights.semibold,
		padding: `0 ${SPACE[16]}px`,
		[BELOW_COLUMNS]: {
			display: 'none',
		},
	},
	hideBelowFullRow: {
		[BELOW_FULL_ROW]: {
			display: 'none',
		},
	},
	categoryCell: {
		minWidth: 0,
		[BELOW_FULL_ROW]: {
			gridColumn: '1 / -1',
		},
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[8],
	},
	// The 4px inset keeps the rows' hover shadow from being clipped by the scroll edge.
	scroller: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: SPACE[8],
		margin: `0 -${SPACE[4]}px`,
		minHeight: 0,
		overflowY: 'auto',
		padding: `0 ${SPACE[4]}px ${SPACE[4]}px`,
		[THEME.queries.md]: {
			flex: 'none',
			overflowY: 'visible',
		},
	},
	row: {
		alignItems: 'center',
		backgroundColor: THEME.colors.background.light,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: THEME.common.borderRadius,
		boxSizing: 'border-box',
		display: 'grid',
		fontSize: fontSizes.sm,
		gap: `${SPACE[8]}px ${SPACE[16]}px`,
		padding: `${SPACE[12]}px ${SPACE[16]}px`,
		position: 'relative',
		transition: 'border-color 120ms ease-out, box-shadow 120ms ease-out',
		':hover': {
			borderColor: THEME.colors.border.main,
			boxShadow: shadowSm,
		},
	},
	rowSelected: {
		borderColor: THEME.colors.primary.main,
		boxShadow: `0 0 0 1px ${THEME.colors.primary.main}`,
		':hover': {
			borderColor: THEME.colors.primary.main,
			boxShadow: `0 0 0 1px ${THEME.colors.primary.main}`,
		},
	},
	callLogGrid: {
		gridTemplateColumns: CALL_LOG_COLUMNS,
		[BELOW_FULL_ROW]: {
			gridTemplateColumns: CALL_LOG_COLUMNS_NARROW,
		},
		...CARD_COLUMNS,
	},
	incomingGrid: {
		gridTemplateColumns: INCOMING_COLUMNS,
		...CARD_COLUMNS,
	},
	openRow: {
		height: 'auto',
		inset: 0,
		opacity: 0,
		padding: 0,
		position: 'absolute',
		width: '100%',
		':focus-visible': {
			opacity: 1,
			outline: `2px solid ${THEME.colors.primary.main}`,
			outlineOffset: 2,
		},
	},
	aboveRow: {
		maxWidth: '100%',
		position: 'relative',
		width: 'fit-content',
		zIndex: 1,
	},
	raised: {
		position: 'relative',
		zIndex: 1,
	},
	company: {
		display: 'flex',
		flexDirection: 'column',
		gap: SPACE[4],
		minWidth: 0,
	},
	companyName: {
		color: THEME.colors.text.main,
		fontWeight: fontWeights.bold,
		height: 'auto',
		justifyContent: 'flex-start',
		padding: 0,
		textAlign: 'left',
		whiteSpace: 'normal',
	},
	plainName: {
		fontWeight: fontWeights.bold,
	},
	linkCell: {
		height: 'auto',
		justifyContent: 'flex-start',
		padding: 0,
	},
	muted: {
		color: THEME.colors.text.light,
	},
	mutedSmall: {
		color: THEME.colors.text.light,
		fontSize: fontSizes.xs,
	},
	recording: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[4],
	},
	recordingIcon: {
		alignItems: 'center',
		display: 'inline-flex',
		height: SPACE[20],
		justifyContent: 'center',
		width: SPACE[20],
	},
	recordingIconSvg: {
		height: SPACE[20],
		width: SPACE[20],
	},
	number: {
		alignItems: 'center',
		display: 'flex',
		fontWeight: fontWeights.bold,
		gap: SPACE[4],
	},
	message: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		flexDirection: 'column',
		fontSize: fontSizes.sm,
		gap: SPACE[12],
		padding: `${SPACE[48]}px ${SPACE[16]}px`,
		textAlign: 'center',
	},
	loadMoreError: {
		alignItems: 'center',
		color: THEME.colors.text.light,
		display: 'flex',
		fontSize: fontSizes.sm,
		gap: SPACE[12],
		justifyContent: 'center',
		padding: SPACE[16],
	},
	skeletonRow: {
		borderRadius: THEME.common.borderRadius,
		height: 64,
	},
});
